import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { TableModule } from 'primeng/table';
import { FollowUpService } from '../../services/follow-up.service';
import { firstValueFrom } from 'rxjs';
import { FollowUp, FuProgress } from '../../models/FollowUpResponse';
import { TagModule } from 'primeng/tag';
import { ProgressBarModule } from 'primeng/progressbar';
import { ModulesService } from '../../services/modules.service';
import { Module } from '../../models/Module';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { DividerModule } from 'primeng/divider';
import { User } from '../../models/User';
import { AuthService } from '../../services/auth.service';
import { ModalService } from '../../services/modal.service';
import { AddAccountModalComponent } from '../modales/add-account-modal/add-account-modal.component';
import { Role } from '../../models/Role';
import { AffectedUsersResponse } from '../../models/AffectedUsersResponse';
import { ChildAddedModaleComponent } from '../modales/child-added-modale/child-added-modale.component';
import { LastValidated } from '../../models/LastValidated';
import { TranslateModule } from '@ngx-translate/core';
import { TranslationService } from '../../services/translation.service';

@Component({
    selector: 'app-follow-up',
    standalone: true,
    imports: [
        CommonModule,
        TableModule,
        TagModule,
        ProgressBarModule,
        ScrollPanelModule,
        DividerModule,
        TranslateModule,
    ],
    templateUrl: './follow-up.component.html',
    styleUrl: './follow-up.component.scss',
})
export class FollowUpComponent {
    user?: User;
    affectedUsers: AffectedUsersResponse | null = null;
    followup: FollowUp[];
    modules: Module[];
    progressInfo?: FuProgress[];
    dates: { [userId: string]: LastValidated } = {};
    Role = Role;

    constructor(
        private modalService: ModalService,
        private authService: AuthService,
        private followUpService: FollowUpService,
        private moduleService: ModulesService,
        public translationService: TranslationService
    ) {}

    async ngOnInit() {
        await this.fetchData();
    }

    async fetchData() {
        const user = await firstValueFrom(
            this.authService.getUserInfoOrRefreshIfNeeded()
        );
        const res = await firstValueFrom(
            this.followUpService.getStudentsFollowUp()
        );
        const modules = await firstValueFrom(
            this.moduleService.getAllModules()
        );

        if (user.is_parent) {
            const affectedUsers = await firstValueFrom(
                this.authService.getAffectedUsers()
            );
            this.affectedUsers = affectedUsers;
        }

        this.user = user;
        this.modules = modules;
        this.followup = res.data;
        this.computeLastDateForUsers();
    }

    computeLastDateForUsers() {
        this.followup.forEach((fu) => {
            const userId = fu.user_id;
            const array = fu.progresses
                .filter((elt) => elt.last_validation_datetime !== null)
                .map((elt) => elt.last_validation_datetime)
                .map((elt) => new Date(elt))
                .sort((a, b) => b.getTime() - a.getTime());

            if (array.length === 0) {
                this.dates[userId] = new LastValidated(undefined, undefined);
            } else {
                const date = array[0];
                const stepDay = Math.floor(
                    (new Date().getTime() - array[0].getTime()) /
                        (1000 * 3600 * 24)
                );
                this.dates[userId] = new LastValidated(date, stepDay);
            }
        });
    }

    shouldDisplaySchoolName(): boolean {
        if (!this.followup) {
            return false;
        }

        return (
            this.followup.findIndex((elt) => {
                let schoolName = elt.user_infos.school_name;

                return (
                    schoolName !== undefined &&
                    schoolName !== null &&
                    schoolName !== ''
                );
            }) !== -1
        );
    }

    getSeverity(
        date: string
    ):
        | 'success'
        | 'secondary'
        | 'info'
        | 'warn'
        | 'danger'
        | 'contrast'
        | undefined {
        if (date === undefined || date === null || date === '') {
            return 'info';
        }

        return this.isPastDate(date) ? 'danger' : 'success';
    }

    getSeverityForLastValidated(
        date: LastValidated
    ):
        | 'success'
        | 'secondary'
        | 'info'
        | 'warn'
        | 'danger'
        | 'contrast'
        | undefined {
        if (date.dayDuration === undefined) {
            return 'danger';
        } else if (date.dayDuration < 4) {
            return 'success';
        } else if (date.dayDuration < 8) {
            return 'warn';
        } else {
            return 'danger';
        }
    }

    getStringForLastValidated(date: LastValidated): string {
        if (date.dayDuration === undefined) {
            return '';
        } else if (date.dayDuration === 0) {
            return this.translationService.instant('followUp.table.today');
        } else {
            return this.translationService.instant('followUp.table.xDays', {
                days: date.dayDuration,
            });
        }
    }

    isPastDate(dateString: string): boolean {
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(year, month - 1, day);

        return date < new Date();
    }

    getGlobalProgressValueFrom(fu: FollowUp): number {
        return (fu.global_score * 100) / fu.global_max_score;
    }

    getProgressValueFrom(progress: FuProgress): number {
        return (progress.score / progress.max_module_score) * 100;
    }

    getModuleName(moduleId: string): string {
        const module = this.modules.find((elt) => elt.module_enum === moduleId);

        return (
            module?.title ??
            this.translationService.instant('followUp.table.unknownModule')
        );
    }

    displayDetails(progress: FuProgress[]) {
        this.progressInfo = progress;
    }

    exitDetails() {
        this.progressInfo = undefined;
    }

    addParent() {
        this.openAddAccountModale(Role.parent);
    }

    addChild() {
        this.openAddAccountModale(Role.enfant);
    }

    openAddAccountModale(role: Role) {
        if (this.shouldDisableAddButton(role)) {
            return;
        }

        const modalRef = this.modalService.open(AddAccountModalComponent, {
            size: 'lg',
        });
        modalRef.componentInstance.role = role;
        modalRef.result.then(async (result) => {
            if (result.message === 'created') {
                const pseudo = result.pseudo;

                await this.fetchData();
                if (!pseudo) {
                    return;
                }

                const user = this.affectedUsers?.children.find(
                    (elt) => elt.pseudo === pseudo
                );
                if (!user) {
                    return;
                }

                const modal = this.modalService.open(
                    ChildAddedModaleComponent,
                    {
                        size: 'lg',
                    }
                );
                modal.componentInstance.user = user;
            }
        });
    }

    shouldDisableAddButton(role: Role): boolean {
        if (this.affectedUsers === null) {
            return true;
        }

        if (role === Role.enfant) {
            return (
                this.affectedUsers.children.length >=
                this.affectedUsers.max_children
            );
        } else {
            return (
                this.affectedUsers.adults.length >=
                this.affectedUsers.max_adults
            );
        }
    }
}
